import { token } from '@/infrastructure/container';
import { EventBus, type EventSubscription } from '@/infrastructure/EventBus';
import { type ILogger } from './Logger';

export type AppBusEvents = {
  TOAST_REQUESTED: { kind: 'success' | 'error' | 'info'; message: string };
  THEME_CHANGED: { theme: 'light' | 'dark' };
  KEY_DOWN: {
    key: string;
    ctrl: boolean;
    meta: boolean;
    shift: boolean;
    alt: boolean;
    preventDefault: () => void;
  };
  // A book finished parsing and was persisted. The player reloads from storage and starts fresh.
  BOOK_LOADED: { title: string };
  // The current book was removed. The player resets to the empty state.
  BOOK_CLEARED: Record<string, never>;
};

export interface IAppBus {
  toast(kind: AppBusEvents['TOAST_REQUESTED']['kind'], message: string): void;
  themeChanged(theme: AppBusEvents['THEME_CHANGED']['theme']): void;
  keyDown(data: AppBusEvents['KEY_DOWN']): void;
  bookLoaded(title: string): void;
  bookCleared(): void;
  subscribe<K extends keyof AppBusEvents>(
    event: K,
    handler: (data: AppBusEvents[K]) => void | Promise<void>,
  ): EventSubscription;
}

export const TOKEN_IAppBus = token<IAppBus>('IAppBus');

export class AppBus implements IAppBus {
  private bus: EventBus<AppBusEvents>;

  constructor(logger: ILogger) {
    this.bus = new EventBus<AppBusEvents>(logger);
  }

  toast(kind: AppBusEvents['TOAST_REQUESTED']['kind'], message: string): void {
    this.bus.publish('TOAST_REQUESTED', { kind, message });
  }

  themeChanged(theme: AppBusEvents['THEME_CHANGED']['theme']): void {
    this.bus.publish('THEME_CHANGED', { theme });
  }

  keyDown(data: AppBusEvents['KEY_DOWN']): void {
    this.bus.publish('KEY_DOWN', data);
  }

  bookLoaded(title: string): void {
    this.bus.publish('BOOK_LOADED', { title });
  }

  bookCleared(): void {
    this.bus.publish('BOOK_CLEARED', {});
  }

  subscribe<K extends keyof AppBusEvents>(
    event: K,
    handler: (data: AppBusEvents[K]) => void | Promise<void>,
  ): EventSubscription {
    return this.bus.subscribe(event, handler);
  }

  // Introspection for boot wiring and tests only. Kept off IAppBus so injected stores can't branch
  // on how many others are listening.
  handlerCount<K extends keyof AppBusEvents>(event: K): number {
    return this.bus.handlerCount(event);
  }
}
